/**
 * News Service Module
 * 
 * This service handles all API interactions for financial news articles.
 * Provides methods for fetching news with optional ticker filtering
 * and cursor-based pagination for infinite scroll.
 */

import type { NewsArticle, NewsResponse } from '@trading-app/shared';
import { apiClient } from './api-client';

// Re-export so existing consumers (e.g. NewsPage) keep importing these
// types from `services/news`.
export type { NewsArticle, NewsResponse };

/**
 * Response from the tickers endpoint
 */
export interface TickersResponse {
  success: boolean;
  tickers: string[];
}

/**
 * Parameters for fetching news
 */
export interface GetNewsParams {
  ticker?: string;
  limit?: number;
  cursor?: string;
}

class NewsService {
  /**
   * Fetch news articles with optional filtering and pagination
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to news articles and next cursor
   */
  async getNews(params: GetNewsParams = {}): Promise<NewsResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.ticker) {
      searchParams.set('ticker', params.ticker);
    }
    if (params.limit) {
      searchParams.set('limit', params.limit.toString());
    }
    if (params.cursor) {
      searchParams.set('cursor', params.cursor);
    }

    const queryString = searchParams.toString();
    const url = `/api/news${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return apiClient.handleResponse<NewsResponse>(response);
  }

  /**
   * Fetch list of unique tickers that have news articles
   * Useful for building filter UI
   * 
   * @returns Promise resolving to array of ticker symbols
   */
  async getTickers(): Promise<string[]> {
    const response = await apiClient.get('/api/news/tickers');
    const data = await apiClient.handleResponse<TickersResponse>(response);
    return data.tickers;
  }
}

// Export singleton instance
export const newsService = new NewsService();

